import { useEffect, useMemo, useState } from "react";
import { Link2, RefreshCw, Download, Filter } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area, Legend,
} from "recharts";
import { supabase } from "../lib/supabase";
import { fmtBRL0, fmtNum, fmtPct } from "../lib/format";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";

/** Paleta consistente com o canalBadge */
const CHANNEL_COLOR: Record<string, string> = {
  "Meta Ads": "#8b5cf6",
  "Google Ads": "#3b82f6",
  "TikTok Ads": "#f43f5e",
  "Instagram (orgânico)": "#ec4899",
  "Facebook (orgânico)": "#0ea5e9",
  "TikTok (orgânico)": "#fb7185",
  "Google (orgânico)": "#10b981",
  "Direto / Não-rastreado": "#9ca3af",
  Outros: "#f59e0b",
};

type GroupBy = "utm_source" | "utm_medium" | "utm_campaign" | "utm_content" | "utm_term";
type WindowDays = 1 | 3 | 7 | 15 | 30;

interface NiaraRow {
  reservation_number: string;
  total: number;
  guest_state: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  source: string | null;
  created_at: string;
}

interface SnapshotKpis { spend_brl?: number; impressions?: number; clicks?: number }
interface Snapshot { channel: "meta" | "google" | "tiktok"; date: string; account_kpis: SnapshotKpis }

// Heurística pra mapear utm_source/medium do tráfego ao gasto do canal.
function channelForUtm(source: string | null, medium: string | null): "meta" | "google" | "tiktok" | null {
  const s = (source || "").toLowerCase();
  const m = (medium || "").toLowerCase();
  if (/meta|facebook|fb|instagram|ig/.test(s)) return "meta";
  if (/google|gads|adwords/.test(s)) return "google";
  if (/tiktok|tt/.test(s)) return "tiktok";
  if (/cpc|paid_social|paidsocial|paid-social/.test(m) && /fb|ig|insta/.test(s)) return "meta";
  return null;
}

/**
 * Badge visual rico pro canal (paid vs organic, com cor por origem).
 * Foco da página é atribuição — o canal precisa saltar aos olhos.
 */
function canalBadge(source: string | null, medium: string | null): {
  label: string;
  cls: string;
  detail: string;
} {
  const s = (source || "").toLowerCase();
  const m = (medium || "").toLowerCase();
  const paid = ["paid", "cpc", "cpv", "cpm", "ppc"].includes(m);
  const organic = ["social", "organic", "referral", "email", "messenger"].includes(m);

  if (/google|gads|adwords/.test(s) && paid) {
    return { label: "Google Ads", detail: `${s} · ${m}`, cls: "bg-blue-100 text-blue-800 border-blue-300" };
  }
  if (/tiktok|tt/.test(s) && paid) {
    return { label: "TikTok Ads", detail: `${s} · ${m}`, cls: "bg-rose-100 text-rose-800 border-rose-300" };
  }
  if (paid && /meta|facebook|fb|instagram|ig/.test(s)) {
    return { label: "Meta Ads", detail: `${s} · ${m}`, cls: "bg-violet-100 text-violet-800 border-violet-300" };
  }
  if (organic && /instagram|ig/.test(s)) {
    return { label: "Instagram (orgânico)", detail: m, cls: "bg-pink-100 text-pink-800 border-pink-300" };
  }
  if (organic && /facebook|fb/.test(s)) {
    return { label: "Facebook (orgânico)", detail: m, cls: "bg-sky-100 text-sky-800 border-sky-300" };
  }
  if (organic && /tiktok|tt/.test(s)) {
    return { label: "TikTok (orgânico)", detail: m, cls: "bg-rose-50 text-rose-700 border-rose-200" };
  }
  if (s === "google" && (m === "organic" || !m)) {
    return { label: "Google (orgânico)", detail: m || "—", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" };
  }
  if (s === "(direto / não-rastreado)" || (!s && !m)) {
    return { label: "Direto / Não-rastreado", detail: "—", cls: "bg-esm-gray-100 text-esm-gray-700 border-esm-gray-200" };
  }
  return { label: s || "—", detail: m || "—", cls: "bg-amber-100 text-amber-800 border-amber-300" };
}

const GROUP_LABELS: Record<GroupBy, string> = {
  utm_source: "Fonte (utm_source)",
  utm_medium: "Mídia (utm_medium)",
  utm_campaign: "Campanha (utm_campaign)",
  utm_content: "Conteúdo (utm_content)",
  utm_term: "Termo (utm_term)",
};

export default function UTMs() {
  const [days, setDays] = useState<WindowDays>(7);
  const [groupBy, setGroupBy] = useState<GroupBy>("utm_source");
  const [search, setSearch] = useState("");
  const [reservations, setReservations] = useState<NiaraRow[]>([]);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const sinceDate = new Date(Date.now() - days * 86400_000).toISOString().slice(0, 10);
      const since = `${sinceDate} 03:00:00+00`; // TZ Recife → 00:00 local
      const [r1, r2] = await Promise.all([
        supabase.from("niara_reservations")
          .select("reservation_number,total,guest_state,utm_source,utm_medium,utm_campaign,utm_content,utm_term,source,created_at")
          .gte("created_at", since)
          .order("created_at", { ascending: false }),
        supabase.from("ads_daily_snapshots")
          .select("channel,date,account_kpis")
          .gte("date", sinceDate),
      ]);
      setReservations((r1.data as NiaraRow[]) ?? []);
      setSnapshots((r2.data as Snapshot[]) ?? []);
      setLoading(false);
    };
    load();
  }, [days, refreshTick]);

  // Gasto agregado por canal no período (Meta/Google/TikTok)
  const spendByChannel = useMemo(() => {
    const m: Record<string, number> = { meta: 0, google: 0, tiktok: 0 };
    for (const s of snapshots) m[s.channel] = (m[s.channel] ?? 0) + (s.account_kpis.spend_brl ?? 0);
    return m;
  }, [snapshots]);

  // Linhas agrupadas por valor da coluna escolhida
  const rows = useMemo(() => {
    const map = new Map<string, {
      key: string;
      reservas: number;
      receita: number;
      cupons: number; // contagem indireta — não tem campo explícito
      utm_source: string | null;
      utm_medium: string | null;
    }>();
    for (const r of reservations) {
      const raw = (r[groupBy] ?? "").trim();
      const valor = raw || "(direto / não-rastreado)";
      // Sub-quebra pelo canal classificado — senão "ig" com paid e social
      // caem no mesmo bucket e o badge sai errado pra metade das reservas.
      const canal = canalBadge(r.utm_source, r.utm_medium).label;
      const key = `${valor}__${canal}`;
      const slot = map.get(key) ?? {
        key, reservas: 0, receita: 0, cupons: 0,
        // Guarda o medium representativo desse bucket — não o do primeiro registro
        utm_source: r.utm_source, utm_medium: r.utm_medium,
      };
      slot.reservas += 1;
      slot.receita += Number(r.total) || 0;
      map.set(key, slot);
    }
    const all = [...map.values()];
    const totalReservas = all.reduce((s, r) => s + r.reservas, 0);
    const totalReceita = all.reduce((s, r) => s + r.receita, 0);

    return all
      .map((r) => {
        const ticket = r.reservas > 0 ? r.receita / r.reservas : 0;
        const ch = channelForUtm(r.utm_source, r.utm_medium);
        const gastoAttrib = ch ? spendByChannel[ch] ?? 0 : 0;
        // Atribuição: se source mapeia a um canal, divide o gasto do canal proporcional ao share
        // de reservas dessa linha dentro do mesmo canal. Caso contrário 0.
        const sameChannelTotal = all
          .filter((x) => channelForUtm(x.utm_source, x.utm_medium) === ch)
          .reduce((s, x) => s + x.reservas, 0);
        const gastoLinha = ch && sameChannelTotal > 0 ? gastoAttrib * (r.reservas / sameChannelTotal) : 0;
        const cpa = r.reservas > 0 && gastoLinha > 0 ? gastoLinha / r.reservas : 0;
        const roas = gastoLinha > 0 ? r.receita / gastoLinha : 0;
        const lucro = r.receita - gastoLinha;
        const margem = r.receita > 0 ? (lucro / r.receita) * 100 : 0;
        return {
          ...r,
          ticket,
          channel: ch,
          gasto: gastoLinha,
          cpa, roas, lucro, margem,
          shareReservas: totalReservas > 0 ? (r.reservas / totalReservas) * 100 : 0,
          shareReceita: totalReceita > 0 ? (r.receita / totalReceita) * 100 : 0,
        };
      })
      .filter((r) => !search || r.key.toLowerCase().includes(search.toLowerCase()) || (r.utm_source || "").toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => b.receita - a.receita);
  }, [reservations, groupBy, spendByChannel, search]);

  const totals = useMemo(() => {
    return rows.reduce((acc, r) => ({
      reservas: acc.reservas + r.reservas,
      receita: acc.receita + r.receita,
      gasto: acc.gasto + r.gasto,
      lucro: acc.lucro + r.lucro,
    }), { reservas: 0, receita: 0, gasto: 0, lucro: 0 });
  }, [rows]);

  const totalRoas = totals.gasto > 0 ? totals.receita / totals.gasto : 0;
  const totalCpa = totals.reservas > 0 && totals.gasto > 0 ? totals.gasto / totals.reservas : 0;

  // KPIs Direto vs Atribuído (canal != direto/não-rastreado)
  const direto = useMemo(() => {
    const d = reservations.filter((r) => !r.utm_source && !r.utm_medium);
    return {
      reservas: d.length,
      receita: d.reduce((s, r) => s + (Number(r.total) || 0), 0),
    };
  }, [reservations]);
  const atribuido = useMemo(() => ({
    reservas: totals.reservas - direto.reservas,
    receita: totals.receita - direto.receita,
  }), [totals, direto]);

  // Agrupamento por CANAL (não pela coluna UTM crua) — usado nos gráficos
  const porCanal = useMemo(() => {
    const map = new Map<string, { canal: string; reservas: number; receita: number; gasto: number; cor: string }>();
    for (const r of reservations) {
      const ch = canalBadge(r.utm_source, r.utm_medium);
      const key = ch.label;
      const cor: string = CHANNEL_COLOR[key] ?? CHANNEL_COLOR.Outros ?? "#9ca3af";
      const slot = map.get(key) ?? { canal: key, reservas: 0, receita: 0, gasto: 0, cor };
      slot.reservas += 1;
      slot.receita += Number(r.total) || 0;
      map.set(key, slot);
    }
    // gasto: distribui spend agregado por canal pago
    const arr = [...map.values()];
    for (const c of arr) {
      if (c.canal === "Meta Ads") c.gasto = spendByChannel.meta ?? 0;
      else if (c.canal === "Google Ads") c.gasto = spendByChannel.google ?? 0;
      else if (c.canal === "TikTok Ads") c.gasto = spendByChannel.tiktok ?? 0;
    }
    return arr.sort((a, b) => b.receita - a.receita);
  }, [reservations, spendByChannel]);

  // Série temporal: reservas/receita por dia, agrupado por canal (até top 5 + Outros)
  const serieTemporal = useMemo(() => {
    // Cria mapa data → {Meta Ads: N, Google Ads: N, ...}
    const top = porCanal.slice(0, 5).map((c) => c.canal);
    const isTop = (canal: string) => top.includes(canal);
    const byDay = new Map<string, Record<string, string | number>>();
    for (const r of reservations) {
      const day = r.created_at.slice(0, 10);
      const ch = canalBadge(r.utm_source, r.utm_medium);
      const bucket = isTop(ch.label) ? ch.label : "Outros";
      const slot = byDay.get(day) ?? { day };
      slot[bucket] = (Number(slot[bucket]) || 0) + 1;
      byDay.set(day, slot);
    }
    return [...byDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, vals]) => ({ ...vals, day: day.slice(5) }));
  }, [reservations, porCanal]);

  const canaisVisiveis = useMemo(() => {
    const set = new Set<string>();
    for (const row of serieTemporal) {
      for (const k of Object.keys(row)) {
        if (k !== "day") set.add(k);
      }
    }
    return [...set];
  }, [serieTemporal]);

  // ROAS por canal pago
  const roasPorCanal = useMemo(() => {
    return porCanal
      .filter((c) => c.gasto > 0)
      .map((c) => ({
        canal: c.canal,
        roas: c.gasto > 0 ? c.receita / c.gasto : 0,
        cpa: c.reservas > 0 && c.gasto > 0 ? c.gasto / c.reservas : 0,
        cor: c.cor,
      }));
  }, [porCanal]);

  const exportCsv = () => {
    const header = ["origem", "canal", "utm_medium", "reservas", "receita", "gasto", "cpa", "roas", "lucro", "margem_pct", "ticket_medio", "share_receita_pct"];
    const lines = [header.join(",")];
    for (const r of rows) {
      const valor = r.key.split("__")[0] ?? r.key;
      const canal = canalBadge(r.utm_source, r.utm_medium).label;
      lines.push([
        `"${valor.replace(/"/g, '""')}"`,
        `"${canal}"`,
        `"${r.utm_medium ?? ""}"`,
        r.reservas, r.receita.toFixed(2), r.gasto.toFixed(2),
        r.cpa.toFixed(2), r.roas.toFixed(2), r.lucro.toFixed(2),
        r.margem.toFixed(2), r.ticket.toFixed(2), r.shareReceita.toFixed(2),
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `utms-${groupBy}-${days}d-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader
        title="Relatório de UTMs"
        subtitle="Atribuição de reservas Niara por origem de tráfego · cruza com gasto de mídia quando UTM mapeia a canal pago"
        icon={<Link2 size={22} strokeWidth={2} />}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setRefreshTick((t) => t + 1)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border border-esm-gray-200 hover:border-esm-brown-300 text-esm-gray-700 text-xs font-bold transition-colors"
            >
              <RefreshCw size={13} /> Atualizar
            </button>
            <button
              onClick={exportCsv}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-esm-brown-500 hover:bg-esm-brown-700 text-white text-xs font-bold transition-colors"
            >
              <Download size={13} /> Exportar CSV
            </button>
          </div>
        }
      />

      {/* Cards de resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <ResumeCard label="Reservas" value={fmtNum(totals.reservas)} hint={`${rows.length} origens distintas`} />
        <ResumeCard label="Receita" value={fmtBRL0(totals.receita)} hint={`Ticket médio ${fmtBRL0(totals.reservas > 0 ? totals.receita / totals.reservas : 0)}`} />
        <ResumeCard label="Gasto atribuído" value={fmtBRL0(totals.gasto)} hint={totals.gasto > 0 ? `CPA ${fmtBRL0(totalCpa)}` : "sem UTM mapeada a ads"} />
        <ResumeCard label="ROAS" value={totalRoas > 0 ? `${totalRoas.toFixed(2).replace(".", ",")}x` : "—"} hint={`Lucro ${fmtBRL0(totals.lucro)}`} highlight />
      </div>

      {/* KPIs Direto vs Atribuído — mesmo estilo do ResumeCard */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <ResumeCard
          label="Reservas atribuídas (com UTM)"
          value={fmtNum(atribuido.reservas)}
          hint={`${fmtBRL0(atribuido.receita)} · ${totals.reservas > 0 ? fmtPct((atribuido.reservas / totals.reservas) * 100, 1) : "—"} do total`}
        />
        <ResumeCard
          label="Diretas (sem UTM)"
          value={fmtNum(direto.reservas)}
          hint={`${fmtBRL0(direto.receita)} · ${totals.reservas > 0 ? fmtPct((direto.reservas / totals.reservas) * 100, 1) : "—"} do total`}
        />
      </div>

      {/* GRÁFICOS — receita por canal + ROAS por canal pago */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card title="Receita por canal" subtitle="Período selecionado">
          <div className="h-72">
            {porCanal.length === 0 ? (
              <div className="h-full flex items-center justify-center text-esm-gray-400 italic text-sm">
                Sem dados no período
              </div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={porCanal}
                    dataKey="receita"
                    nameKey="canal"
                    cx="50%"
                    cy="50%"
                    outerRadius={88}
                    innerRadius={50}
                    paddingAngle={2}
                    labelLine={false}
                    label={(p) => {
                      const pct = totals.receita > 0 ? (p.receita / totals.receita) * 100 : 0;
                      return pct >= 5 ? `${pct.toFixed(0)}%` : "";
                    }}
                  >
                    {porCanal.map((c) => (
                      <Cell key={c.canal} fill={c.cor} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E8E3DB" }}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    formatter={(v: any, _: any, p: any) =>
                      [`${fmtBRL0(Number(v))} · ${fmtNum(p?.payload?.reservas ?? 0)} reservas`, p?.payload?.canal ?? ""]
                    }
                  />
                  <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                    iconSize={9}
                    wrapperStyle={{ fontSize: 11, color: "#9B8E7D" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card title="ROAS por canal pago" subtitle="Apenas canais com gasto mapeado">
          <div className="h-72">
            {roasPorCanal.length === 0 ? (
              <div className="h-full flex items-center justify-center text-esm-gray-400 italic text-sm text-center px-6">
                Nenhum canal pago com gasto mapeado no período
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={roasPorCanal} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DB" />
                  <XAxis dataKey="canal" fontSize={10} stroke="#9B8E7D" interval={0} />
                  <YAxis fontSize={10} stroke="#9B8E7D" tickFormatter={(v) => `${v.toFixed(1)}x`} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E8E3DB" }}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    formatter={(v: any, k: any, p: any) => {
                      if (k === "roas") return [`${Number(v).toFixed(2).replace(".", ",")}x  ·  CPA ${fmtBRL0(p?.payload?.cpa ?? 0)}`, p?.payload?.canal ?? ""];
                      return [v, k];
                    }}
                  />
                  <Bar dataKey="roas" radius={[6, 6, 0, 0]}>
                    {roasPorCanal.map((c) => (
                      <Cell key={c.canal} fill={c.cor} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      {/* Série temporal — reservas por dia por canal */}
      <Card title="Reservas por dia · por canal" subtitle="Stacked area · top 5 canais + Outros" className="mb-4">
        <div className="h-72">
          {serieTemporal.length === 0 ? (
            <div className="h-full flex items-center justify-center text-esm-gray-400 italic text-sm">
              Sem dados
            </div>
          ) : (
            <ResponsiveContainer>
              <AreaChart data={serieTemporal} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DB" />
                <XAxis dataKey="day" fontSize={10} stroke="#9B8E7D" />
                <YAxis fontSize={10} stroke="#9B8E7D" allowDecimals={false} />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E8E3DB" }}
                />
                <Legend iconSize={9} wrapperStyle={{ fontSize: 11, color: "#9B8E7D" }} />
                {canaisVisiveis.map((canal) => (
                  <Area
                    key={canal}
                    type="monotone"
                    dataKey={canal}
                    stackId="1"
                    stroke={CHANNEL_COLOR[canal] || CHANNEL_COLOR.Outros}
                    fill={CHANNEL_COLOR[canal] || CHANNEL_COLOR.Outros}
                    fillOpacity={0.7}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* Filtros */}
      <Card padded={false}>
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-esm-gray-100">
          <Filter size={14} className="text-esm-gray-400" />
          <FilterGroup
            label="Período"
            value={String(days)}
            options={[
              { value: "1", label: "Hoje" },
              { value: "3", label: "3 dias" },
              { value: "7", label: "7 dias" },
              { value: "15", label: "15 dias" },
              { value: "30", label: "30 dias" },
            ]}
            onChange={(v) => setDays(Number(v) as WindowDays)}
          />
          <FilterGroup
            label="Agrupar por"
            value={groupBy}
            options={(Object.keys(GROUP_LABELS) as GroupBy[]).map((k) => ({ value: k, label: GROUP_LABELS[k] }))}
            onChange={(v) => setGroupBy(v as GroupBy)}
          />
          <div className="ml-auto flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filtrar origem…"
              className="px-3 py-1.5 text-xs border border-esm-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-esm-brown-500/30 focus:border-esm-brown-300 w-48"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-esm-gray-400 italic">Carregando…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-esm-gray-400 italic">
            Nenhuma reserva com {GROUP_LABELS[groupBy]} no período.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-esm-cream-50">
                <tr className="text-left text-esm-gray-500 text-[11px] uppercase tracking-wider">
                  <th className="p-3">{GROUP_LABELS[groupBy]}</th>
                  <th className="p-3 text-right">Reservas</th>
                  <th className="p-3 text-right">Receita</th>
                  <th className="p-3 text-right">% Receita</th>
                  <th className="p-3 text-right">Ticket médio</th>
                  <th className="p-3 text-right">Gasto</th>
                  <th className="p-3 text-right">CPA</th>
                  <th className="p-3 text-right">ROAS</th>
                  <th className="p-3 text-right">Lucro</th>
                  <th className="p-3 text-right">Margem</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-t border-esm-gray-100 hover:bg-esm-cream-50/40">
                    <td className="p-3">
                      {(() => {
                        const ch = canalBadge(r.utm_source, r.utm_medium);
                        // r.key tem o formato "valor__canal" — extrai só o valor pra display
                        const valor = r.key.split("__")[0] ?? r.key;
                        return (
                          <div className="flex flex-col gap-1.5">
                            <span className={`inline-block px-2.5 py-1 rounded-md border text-[11px] font-bold uppercase tracking-wide w-fit ${ch.cls}`}>
                              {ch.label}
                            </span>
                            <p className="text-[11px] text-esm-gray-500 leading-tight">
                              <span className="font-mono">{valor}</span>
                              {r.utm_medium && <span> · {r.utm_medium}</span>}
                            </p>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-3 text-right tabular-nums">{fmtNum(r.reservas)}</td>
                    <td className="p-3 text-right tabular-nums font-semibold text-esm-brown-700">{fmtBRL0(r.receita)}</td>
                    <td className="p-3 text-right tabular-nums text-esm-gray-500">{fmtPct(r.shareReceita, 1)}</td>
                    <td className="p-3 text-right tabular-nums">{fmtBRL0(r.ticket)}</td>
                    <td className="p-3 text-right tabular-nums">{r.gasto > 0 ? fmtBRL0(r.gasto) : "—"}</td>
                    <td className="p-3 text-right tabular-nums">{r.cpa > 0 ? fmtBRL0(r.cpa) : "—"}</td>
                    <td className="p-3 text-right tabular-nums font-bold">{r.roas > 0 ? `${r.roas.toFixed(2).replace(".", ",")}x` : "—"}</td>
                    <td className={`p-3 text-right tabular-nums font-semibold ${r.lucro >= 0 ? "text-esm-success" : "text-esm-error"}`}>{fmtBRL0(r.lucro)}</td>
                    <td className={`p-3 text-right tabular-nums ${r.margem >= 0 ? "text-esm-gray-700" : "text-esm-error"}`}>{r.gasto > 0 ? fmtPct(r.margem, 1) : "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-esm-gray-200 bg-esm-cream-50 font-bold">
                  <td className="p-3 text-esm-gray-900">Total ({rows.length})</td>
                  <td className="p-3 text-right tabular-nums">{fmtNum(totals.reservas)}</td>
                  <td className="p-3 text-right tabular-nums text-esm-brown-700">{fmtBRL0(totals.receita)}</td>
                  <td className="p-3"></td>
                  <td className="p-3 text-right tabular-nums">{fmtBRL0(totals.reservas > 0 ? totals.receita / totals.reservas : 0)}</td>
                  <td className="p-3 text-right tabular-nums">{fmtBRL0(totals.gasto)}</td>
                  <td className="p-3 text-right tabular-nums">{totalCpa > 0 ? fmtBRL0(totalCpa) : "—"}</td>
                  <td className="p-3 text-right tabular-nums">{totalRoas > 0 ? `${totalRoas.toFixed(2).replace(".", ",")}x` : "—"}</td>
                  <td className={`p-3 text-right tabular-nums ${totals.lucro >= 0 ? "text-esm-success" : "text-esm-error"}`}>{fmtBRL0(totals.lucro)}</td>
                  <td className="p-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Aviso de captura */}
      <div className="mt-4 flex items-start gap-3 bg-esm-cream-50 border border-esm-brown-100 rounded-2xl p-4 text-[12px] text-esm-brown-900">
        <Link2 size={16} className="text-esm-brown-700 mt-0.5 flex-shrink-0" />
        <div className="space-y-1">
          <p className="font-bold">Como funciona a atribuição</p>
          <p className="leading-relaxed">
            Reservas com UTM são identificadas pelo cookie <code className="bg-white px-1 rounded">eph_utms</code> (90 dias, first-touch).
            Gasto e ROAS são calculados quando <code className="bg-white px-1 rounded">utm_source</code> mapeia a um canal pago
            (Meta / Google / TikTok). Reservas sem UTM aparecem como "direto / não-rastreado".
          </p>
          <p className="leading-relaxed">
            <b>Pra ter mais atribuição:</b> use o <a href="https://geradores.esmeraldapraiahotel.com.br" className="underline" target="_blank" rel="noreferrer">Construtor de UTM</a> (em construção) pra
            padronizar links de WhatsApp, RD Conversas, email, Instagram orgânico, etc.
          </p>
        </div>
      </div>
    </>
  );
}

function ResumeCard({ label, value, hint, highlight = false }: { label: string; value: string; hint?: string; highlight?: boolean }) {
  return (
    <div className={`rounded-2xl p-5 shadow-card border ${highlight ? "bg-gradient-to-br from-esm-brown-700 to-esm-brown-900 text-white border-esm-brown-900" : "bg-white border-esm-gray-100"}`}>
      <p className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${highlight ? "text-esm-cream-100/80" : "text-esm-gray-400"}`}>{label}</p>
      <p className={`text-2xl font-bold tabular-nums leading-tight ${highlight ? "text-white" : "text-esm-gray-900"}`}>{value}</p>
      {hint && <p className={`text-[11px] mt-1 ${highlight ? "text-esm-cream-100/70" : "text-esm-gray-500"}`}>{hint}</p>}
    </div>
  );
}

function FilterGroup({ label, value, options, onChange }: { label: string; value: string; options: { value: string; label: string }[]; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider font-bold text-esm-gray-400">{label}:</span>
      <div className="flex gap-1 bg-esm-cream-50 rounded-lg p-0.5">
        {options.map((o) => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={`px-2.5 py-1 text-xs font-bold rounded transition-colors ${value === o.value ? "bg-white text-esm-brown-700 shadow-sm" : "text-esm-gray-600 hover:text-esm-gray-900"}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
